/**
 * The lead channel, client side.
 *
 * Until 2026-08-05 this module recorded submissions in the visitor's own browser and sent nothing,
 * because creating a live contact channel needed founder approval (ADR-005). That approval is now
 * closed as D-021, so it posts to `functions/api/lead.ts`, which writes to D1 and notifies.
 *
 * The local copy did not go away when the endpoint arrived. A visitor who has just written out how
 * their operation actually works should not lose that to a network failure, so the record is
 * written locally first and the request happens after. If the request fails, the submission is
 * still on their machine and the form says plainly that it did not go through, a form that
 * reports success on a dropped submission is worse than one that reports failure.
 */

const STORAGE_KEY = 'civili.site.leads';
const ENDPOINT = '/api/lead';
const TIMEOUT_MS = 12000;

export interface LeadRecord {
  readonly id: string;
  readonly receivedAt: string;
  /** Where the record actually went. `local` means the network leg did not complete. */
  readonly delivery: 'sent' | 'local';
  readonly fields: Readonly<Record<string, string>>;
}

export class LeadDeliveryError extends Error {
  constructor(readonly reason: 'network' | 'rejected' | 'storage') {
    super(reason);
    this.name = 'LeadDeliveryError';
  }
}

function newId(): string {
  const stamp = new Date().toISOString().slice(0, 10).replaceAll('-', '');
  const suffix = Math.random().toString(36).slice(2, 8).toUpperCase();
  return `CIV-LEAD-${stamp}-${suffix}`;
}

/**
 * Append the record locally. Private browsing, a full quota or a blocked storage API all throw
 * here, and none of them should cost the visitor the message they just wrote, so the write is
 * best-effort and its failure never propagates.
 */
function persist(record: LeadRecord): void {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const existing = raw ? (JSON.parse(raw) as LeadRecord[]) : [];
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify([...existing, record]));
  } catch {
    // Intentionally silent. The submission still goes to the server, which is the record that
    // matters; the local copy is a courtesy.
  }
}

export async function recordLead(data: FormData, language: string): Promise<LeadRecord> {
  const fields: Record<string, string> = { idioma: language };
  for (const [key, value] of data.entries()) {
    if (typeof value === 'string') fields[key] = value.trim();
  }

  const local: LeadRecord = {
    id: newId(),
    receivedAt: new Date().toISOString(),
    delivery: 'local',
    fields
  };
  persist(local);

  // A hung request must not leave the visitor watching a spinner with no way to read the outcome.
  const abort = new AbortController();
  const timer = window.setTimeout(() => abort.abort(), TIMEOUT_MS);

  let response: Response;
  try {
    response = await fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(fields),
      signal: abort.signal
    });
  } catch {
    throw new LeadDeliveryError('network');
  } finally {
    window.clearTimeout(timer);
  }

  if (response.status === 503) throw new LeadDeliveryError('storage');
  if (!response.ok) throw new LeadDeliveryError('rejected');

  const body = (await response.json().catch(() => ({}))) as { id?: string };
  return { ...local, id: body.id ?? local.id, delivery: 'sent' };
}

/** Every lead recorded in this browser, newest last. A local courtesy copy, not the record. */
export function storedLeads(): readonly LeadRecord[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as LeadRecord[]) : [];
  } catch {
    return [];
  }
}
